import React, { FC, ReactNode } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  SxProps,
} from 'portal-commons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons';

interface SectionCardProps {
  title: ReactNode;
  icon?: IconDefinition;
  description?: ReactNode;
  onEdit?: () => void;
  editable?: boolean;
  customButton?: ReactNode;
  sx?: SxProps;
  dataTestId?: string;
  children?: ReactNode;
}

const SectionCard: FC<SectionCardProps> = ({
  title,
  icon,
  description,
  onEdit,
  editable = false,
  sx,
  customButton,
  children,
  dataTestId,
}) => {
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        border: '1px solid #C0CCD4',
        padding: '30px 31px 35px',
        width: '100%',
        ...sx,
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Left: Icon and Title */}
          <Flex sx={{ alignItems: 'center', gap: '8px' }}>
            {icon && (
              <Box
                as="span"
                sx={{
                  fontSize: '24px',
                  color: '#4D5659',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FontAwesomeIcon icon={icon} />
              </Box>
            )}
            <Box
              as="h2"
              sx={{
                fontSize: '18px',
                fontWeight: 800,
                color: '#4D5659',
              }}
            >
              {title}
            </Box>
          </Flex>

          {/* Right: Edit Button || Custom Button */}
          {editable && (
            <Button
              data-testid={dataTestId && `${dataTestId}_editIcon`}
              variant="transparent"
              shape="square"
              startAdornment={faPen}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
          {customButton && (
            <Box data-testid={dataTestId && `${dataTestId}_button`}>
              {customButton}
            </Box>
          )}
        </Flex>

        {/* Separator line */}
        <Box sx={{ borderBottom: '1px solid #CCCFD0', width: '100%' }} />

        {/* Description */}
        {description && (
          <Box
            sx={{
              fontSize: '14px',
              color: '#4D5659',
              fontFamily: 'Roboto',
              fontWeight: 400,
              lineHeight: 'normal',
              mt: '8px',
            }}
          >
            {description}
          </Box>
        )}

        {/* Content Area */}
        <Box sx={{ width: '100%', mt: '12px' }}>{children}</Box>
      </Flex>
    </Box>
  );
};

export default SectionCard;
